// Find/replace section for Models bulk meta edit modal.
import type { RecipeMetaRenameField } from "@/lib/manufacturing/recipeMetaRenamePreview";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export type BulkRenamePreview = {
  matchedCount: number;
  rows: Array<{
    id: string;
    code: string;
    name: string;
    nextCode: string;
    nextName: string;
    matched: boolean;
  }>;
  blockers: Array<{ id: string; code: string; error: string }>;
};

export function ManufacturingModelsBulkRenameSection({
  enabled,
  onEnabledChange,
  canEditCode,
  renameField,
  onRenameFieldChange,
  renameFind,
  onRenameFindChange,
  renameReplace,
  onRenameReplaceChange,
  renamePreview,
}: {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  canEditCode: boolean;
  renameField: RecipeMetaRenameField;
  onRenameFieldChange: (v: RecipeMetaRenameField) => void;
  renameFind: string;
  onRenameFindChange: (v: string) => void;
  renameReplace: string;
  onRenameReplaceChange: (v: string) => void;
  renamePreview: BulkRenamePreview | null;
}) {
  return (
    <div className="space-y-2 rounded-md border border-gray-200 p-3 dark:border-zinc-700">
      <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-800 dark:text-zinc-200">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
        Find / replace in name{canEditCode ? " or code" : ""}
      </label>
      {enabled ? (
        <>
          <label className="block text-xs text-gray-600 dark:text-zinc-400">
            Field
            <select
              className={`${inputClass} mt-1`}
              value={renameField}
              onChange={(e) => onRenameFieldChange(e.target.value as RecipeMetaRenameField)}
            >
              <option value="name">Name</option>
              {canEditCode ? <option value="code">Code</option> : null}
              {canEditCode ? <option value="both">Name and code</option> : null}
            </select>
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-xs text-gray-600 dark:text-zinc-400">
              Find
              <input
                className={`${inputClass} mt-1`}
                value={renameFind}
                onChange={(e) => onRenameFindChange(e.target.value)}
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-400">
              Replace with
              <input
                className={`${inputClass} mt-1`}
                value={renameReplace}
                onChange={(e) => onRenameReplaceChange(e.target.value)}
              />
            </label>
          </div>
          {renamePreview ? (
            <div className="text-[11px] text-gray-600 dark:text-zinc-400">
              {renamePreview.matchedCount} match
              {renamePreview.matchedCount === 1 ? "" : "es"}
              {renamePreview.rows.filter((r) => r.matched).slice(0, 8).map((r) => (
                <div key={r.id} className="mt-0.5 font-mono">
                  {renameField !== "code" ? (
                    <span>
                      {r.name} → {r.nextName}
                    </span>
                  ) : null}
                  {renameField === "both" ? " · " : null}
                  {renameField !== "name" ? (
                    <span>
                      {r.code} → {r.nextCode}
                    </span>
                  ) : null}
                </div>
              ))}
              {renamePreview.matchedCount > 8 ? (
                <div>…and {renamePreview.matchedCount - 8} more</div>
              ) : null}
              {renamePreview.blockers.map((b) => (
                <div key={b.id} className="text-red-600 dark:text-red-400">
                  {b.code}: {b.error}
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
